import React, {useId, useState} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import PropTypes from 'prop-types'
import {Box, useBreakpointValue, List, ListItem, Button} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import PDCImage from '../../../components/pdc-image/index.jsx'
import {useEffect} from 'react'
import Carousel from '../../../components/carousel/index.jsx'
import {ArrowButton} from '../../../components/arrow-button/index.jsx'

/**
 * PDCCollaborationPush is a React functional component designed to render a carousel-based layout for the PDC/PDP Collaboration Push section.
 *
 * This component displays a set of tiles, sourced from the provided `regions` prop, within a carousel slider. It also features a thumbnail navigation section for enhanced user interaction. The carousel supports navigation arrows and keeps track of the current slide. The component is styled responsively and is accessible, with proper ARIA attributes.
 *
 * Props:
 * @param {Object}   props
 * @param {string}   props.title            - The title for the collaboration push section, passed to the thumbnails component.
 * @param {Array}    props.regions          - An array of region objects, where the first region's `components` array is used as the set of tiles to display.
 * @param {number}   props.displayPriority  - A numeric value indicating the display priority, which is passed down to each tile and the thumbnails.
 *
 * Features:
 * - Renders a carousel of tiles using the `Carousel` component, with custom navigation arrows.
 * - Displays a thumbnail navigation section via `PDCCollaborationPushThumbnails`.
 * - Responsive layout and styling using the `Box` component.
 * - Maintains and synchronizes carousel state, including the current slide and swiper instance.
 * - Accessible section with ARIA attributes.
 *
 * Usage:
 * Typically used within a page designer context to highlight collaborative or promotional content in a visually engaging, interactive slider format.
 */
export const PDCCollaborationPush = ({title, regions, displayPriority}) => {
    // Get the tiles from the regions
    let tiles = []
    if (regions) {
        tiles = regions[0].components || []
    }

    const titleId = useId()

    const [nav1, setNav1] = useState(null)
    const [currentSlide, setCurrentSlide] = useState(0)
    const [swiper, setSwiper] = useState(null)

    useEffect(() => {
        setNav1(swiper)
    }, [swiper])

    const sliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        prevArrow: <ArrowButton direction="prev" onClick={() => swiper?.slickPrev()} />,
        nextArrow: <ArrowButton direction="next" onClick={() => swiper?.slickNext()} />,
        onReInit: () => setCurrentSlide(swiper?.innerSlider.state.currentSlide)
    }

    return (
        <Box
            as="section"
            aria-describedby={titleId}
            width="100%"
            backgroundColor={'white'}
            color={'fullBlack'}
            pb={[70, null, null, 120]}
            css={[PDCCollaborationPush.css]}
            position={'relative'}
        >
            <Carousel
                settings={sliderSettings}
                asNavFor={nav1}
                setSwiper={setSwiper}
                nbSlidesToShowByBreakpoint={{base: 1, md: 1, lg: 1, xl: 1}}
            >
                {tiles.map((tile, index) => {
                    tile.displayPriority = displayPriority
                    return <Component key={tile?.id || index} component={tile} />
                })}
            </Carousel>

            <Box
                p={[10, null, null, 20]}
                w={['100%', null, '50%']}
                position={['static', null, null, 'absolute']}
                bottom={['auto', null, null, '120px']}
                mr={['auto', null, 0, 'auto']}
                ml={'auto'}
                pl={[10, null, 5, 20]}
            >
                <PDCCollaborationPushThumbnails
                    imageComponents={tiles}
                    swiper={swiper}
                    currentSlide={currentSlide}
                    displayPriority={displayPriority}
                    title={title}
                />
            </Box>
        </Box>
    )
}

PDCCollaborationPush.css = css`
    .slick-arrow {
        opacity: 0;
        position: absolute;
        left: 20px;
        right: auto;
        bottom: auto;
        top: 50%;
        width: 40px;
        height: 40px;
        background: white;
        z-index: 1;
        transform: translateY(-50%);
        color: var(--chakra-colors-fullBlack);
        border: 1px solid var(--chakra-colors-gray);
        &::before {
            content: '';
        }
        &:focus {
            opacity: 1;
        }
    }
    .slick-next {
        left: auto;
        right: 20px;
    }
`

PDCCollaborationPush.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    supTitle: PropTypes.string,
    desc: PropTypes.string,
    custom: PropTypes.object,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    ),
    displayPriority: PropTypes.bool
}

/**
 * PDCCollaborationPushImage is a React functional component that renders a responsive image
 * for the PDC PDP Collaboration Push layout. It dynamically selects the appropriate image source
 * based on the current breakpoint, supporting multiple image sizes (small, medium, large, xlarge, xxlarge)
 * and custom image links if provided. The component also passes through display priority, title,
 * and image width configurations, and supports additional props for further customization.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.image - The image object containing various image sizes and metadata.
 * @param {number} [props.displayPriority] - Optional display priority for the image.
 * @param {string} [props.title] - Optional title to display with the image.
 * @param {Array|Object} [props.imageWidths] - Optional widths for responsive image rendering.
 * @param {...Object} [props] - Additional props to be passed to the underlying PDCImage component.
 *
 * @returns {JSX.Element} The rendered responsive image component with skeleton loading and accessibility attributes.
 */
const PDCCollaborationPushImage = ({image, displayPriority, title, imageWidths, ...props}) => {
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: image?.custom?.smallImage?.disBaseLink || image?.smallImage,
            mediumEl: image?.custom?.mediumImage?.disBaseLink || image?.mediumImage,
            largeEl: image?.custom?.largeImage?.disBaseLink || image?.largeImage,
            xlargeEl: image?.custom?.xlargeImage?.disBaseLink || image?.xlargeImage,
            xxlargeEl: image?.custom?.xxlargeImage?.disBaseLink || image?.xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    return (
        <PDCImage
            currentImage={currentImage}
            imageWidths={imageWidths}
            imageAlt={image?.data?.imageAlt}
            imageTitle={image?.data?.imageTitle}
            imageProps={{height: '100%'}}
            displayPriority={displayPriority}
            title={title}
            skeletonProps={{
                aspectRatio: ['197 / 240', null, '139 / 180'],
                width: '100%'
            }}
            {...props}
        />
    )
}

PDCCollaborationPushImage.propTypes = {
    image: PropTypes.object,
    displayPriority: PropTypes.bool,
    title: PropTypes.string,
    imageWidths: PropTypes.array
}

/**
 * Renders a horizontal list of thumbnail images for the PDC Collaboration Push feature, allowing users to navigate between slides.
 *
 * @component
 * @param {Object[]} imageComponents - Array of image component objects to be displayed as thumbnails.
 * @param {Object} swiper - Swiper instance used to control slide navigation.
 * @param {number} currentSlide - Index of the currently active slide.
 * @param {number} displayPriority - Priority value used for image display ordering or styling.
 * @param {string} title - Default title used for thumbnail aria-labels if not provided in image data.
 *
 * @returns {JSX.Element} A flex list of clickable thumbnail images, each wrapped in a button for accessibility and navigation.
 *
 * @example
 * <PDCCollaborationPushThumbnails
 *   imageComponents={images}
 *   swiper={swiperInstance}
 *   currentSlide={2}
 *   displayPriority={1}
 *   title="Product Thumbnails"
 * />
 */
const PDCCollaborationPushThumbnails = ({
    imageComponents,
    swiper,
    currentSlide,
    displayPriority,
    title
}) => {
    return (
        <List display={'flex'} gap={10} alignItems={'flex-end'}>
            {imageComponents.map((image, idx) => {
                return (
                    <ListItem
                        key={`thumbnail-${image?.id}-${image?.custom?.disBaseLink || image?.link}`}
                        onClick={() => {
                            swiper?.slickGoTo(idx)
                        }}
                        width={[90, null, '20.75%']}
                        display={'flex'}
                    >
                        <Button
                            variant="unstyled"
                            width="100%"
                            height="100%"
                            aria-label={
                                image?.data.imageTitle === 'empty'
                                    ? ''
                                    : image?.data.imageTitle || title || `Thumbnail ${idx + 1}`
                            }
                            position={'relative'}
                            _after={{
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                cursor: 'pointer',
                                zIndex: 1,
                                borderColor: 'fullBlack',
                                borderWidth: currentSlide === idx ? [1.5, null, 2] : 0,
                                borderStyle: 'solid'
                            }}
                        >
                            <PDCCollaborationPushImage
                                image={image}
                                displayPriority={displayPriority}
                                title={title}
                                imageWidths={[90, 90, 141]}
                                display="flex"
                            />
                        </Button>
                    </ListItem>
                )
            })}
        </List>
    )
}

PDCCollaborationPushThumbnails.propTypes = {
    imageComponents: PropTypes.array,
    swiper: PropTypes.object,
    currentSlide: PropTypes.number,
    displayPriority: PropTypes.bool,
    title: PropTypes.string
}
